import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { secretsApi } from '@/lib/api/secrets';
import { SecretScope, SecretResponse } from '@optidev/shared-types';
import toast from 'react-hot-toast';
import { Checkbox } from '@/components/ui/checkbox';

interface ImportFromAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  projectId: string;
}

export default function ImportFromAccountModal({
  isOpen,
  onClose,
  onSuccess,
  projectId,
}: ImportFromAccountModalProps) {
  const { getIdToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [accountSecrets, setAccountSecrets] = useState<SecretResponse[]>([]);
  const [linkedSecretIds, setLinkedSecretIds] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      loadSecrets();
    }
  }, [isOpen]);

  const loadSecrets = async () => {
    try {
      const token = await getIdToken();
      if (!token) return;

      // Load account secrets
      const accountResponse = await secretsApi.getAll(token, SecretScope.ACCOUNT);
      setAccountSecrets(accountResponse.secrets);

      // Load project secrets to find already-linked ones
      const projectResponse = await secretsApi.getAll(token, SecretScope.PROJECT, projectId);
      const linkedIds = new Set(
        projectResponse.secrets
          .filter(s => s.isLinked)
          .map(s => s.linkedFromAccountId || s.id)
      );
      setLinkedSecretIds(linkedIds);
    } catch (error) {
      console.error('Failed to load secrets:', error);
      toast.error('Failed to load secrets');
    }
  };

  const handleToggle = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedIds.length === 0) {
      toast.error('Please select at least one secret');
      return;
    }

    try {
      setLoading(true);
      const token = await getIdToken();
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const result = await secretsApi.link(token, {
        projectId,
        secretIds: selectedIds,
      });

      setSelectedIds([]);
      toast.success(`Linked: ${result.created} created, ${result.skipped} skipped`);
      onSuccess();
    } catch (error: any) {
      console.error('Failed to link secrets:', error);
      toast.error(error.response?.data?.message || 'Failed to link secrets');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Link from Account</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Link account-level secrets to this project. Changes to account secrets will cascade automatically.
          </p>

          {accountSecrets.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No account-level secrets found
            </p>
          ) : (
            <div className="space-y-1">
              {accountSecrets.map((secret) => {
                const isLinked = linkedSecretIds.has(secret.id);
                return (
                  <div
                    key={secret.id}
                    className={`flex items-center gap-3 p-3 rounded-md border ${
                      isLinked
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-muted/50 cursor-pointer'
                    }`}
                  >
                    <Checkbox
                      checked={isLinked || selectedIds.includes(secret.id)}
                      disabled={isLinked}
                      onCheckedChange={() => !isLinked && handleToggle(secret.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="font-medium truncate">{secret.key}</div>
                        {isLinked && (
                          <span className="text-xs px-2 py-1 rounded bg-primary/10 text-primary-700 dark:text-primary-400">
                            Linked
                          </span>
                        )}
                      </div>
                      {secret.description && (
                        <div className="text-sm text-muted-foreground mt-1 truncate">
                          {secret.description}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex gap-2 justify-end pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || selectedIds.length === 0}
            >
              {loading ? 'Linking...' : `Link ${selectedIds.length} Secret${selectedIds.length !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
